import { ApiPermission } from "@bvhi/backend-api/apis";
import { Box, Button, List, ListItemIcon, ListItemText, Paper, RouterListItemButton } from "@bvhi/ui-base";
import { AddIcon, KeyboardReturnIcon } from "@bvhi/ui-icons";
import { useMatchRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BVHIRoute } from "../config/navigation";
import { useAuthContext } from "../context/use-auth-context";
import { useInviteAudiologistContext } from "../context/use-invite-audiologist-context";
import { usePermissions } from "../hooks/use-permissions";
import { DrawerLinkButton } from "./drawer-link-button";
import { hasFeatureEnabled } from "../utils/env-helper.ts";

interface Props {
  navigationConfig: BVHIRoute[];
}

export const BVHIDrawer = ({ navigationConfig }: Props) => {
  const { canNavigateTo } = usePermissions();
  const matchRoute = useMatchRoute();
  const { t } = useTranslation(["common", "audiologists", "toasts"]);
  const { currentUser } = useAuthContext();
  const isInAppView = matchRoute({ to: "/app", fuzzy: true });
  const { setOpen: setAddAudiologistToSupplierDialogOpen } = useInviteAudiologistContext();
  const { hasPermissionTo } = usePermissions();

  const drawerItems = useMemo(() => {
    let entries = navigationConfig.filter((navigationEntry) => {
      if (!navigationEntry.shouldAppearInDrawer) return false;
      return canNavigateTo(navigationEntry);
    });

    const orderApiEnabled = hasFeatureEnabled("ENABLE_ORDER_API");
    const cr3AudiologistEnabled = hasFeatureEnabled("ENABLE_CR3_AUDIOLOGIST");

    const shouldShowPosAndSettings = currentUser?.type === "AUDIOLOGIST" ? orderApiEnabled && cr3AudiologistEnabled : orderApiEnabled;

    // TODO Remove when order system is in production
    if (currentUser?.type === "SUPPLIER" && !hasFeatureEnabled("ENABLE_ORDER_API")) {
      entries = entries.filter((entry) => entry.path && !entry.path.includes("api-token"));
    }

    if (!shouldShowPosAndSettings) {
      entries = entries.filter((entry) => entry.path && !entry.path.includes("pos"));
    }

    if (!shouldShowPosAndSettings) {
      entries = entries.filter((entry) => entry.path && !entry.path.includes("settings"));
    }

    if (!shouldShowPosAndSettings) {
      entries = entries.filter((entry) => entry.path && !entry.path.includes("messages"));
    }

    return entries;
  }, [navigationConfig, canNavigateTo, currentUser]);

  const canInvite = useMemo(() => {
    return hasPermissionTo([ApiPermission["verification-code:invite"]]);
  }, [hasPermissionTo]);

  return (
    <>
      <Paper
        data-testid="bvhi-drawer"
        square
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ width: "100%", position: "sticky", pt: 2, top: 0 }}>
          <List sx={{ py: 0 }}>
            {currentUser && !isInAppView && (
              <RouterListItemButton sx={{ userSelect: "none" }} to="/app/dashboard">
                <ListItemIcon>
                  <KeyboardReturnIcon />
                </ListItemIcon>

                <ListItemText primary={t("common:bvhi.drawer.back.to.app")} />
              </RouterListItemButton>
            )}
            {!currentUser && (
              <RouterListItemButton sx={{ userSelect: "none" }} to="/">
                <ListItemIcon>
                  <KeyboardReturnIcon />
                </ListItemIcon>

                <ListItemText primary={t("common:bvhi.drawer.back.to.homepage")} />
              </RouterListItemButton>
            )}
            {drawerItems.map((navigationEntry) => {
              return <DrawerLinkButton key={navigationEntry.id} route={navigationEntry} />;
            })}
          </List>
        </Box>
        {canInvite && (
          <Box sx={{ width: "100%", position: "sticky", marginTop: "auto", pb: 2, bottom: 0, px: 2 }}>
            <Button onClick={() => setAddAudiologistToSupplierDialogOpen(true)} size="small" startIcon={<AddIcon />}>
              {t("audiologists:audiologists.invite")}
            </Button>
          </Box>
        )}
      </Paper>
    </>
  );
};
